import useSWRMutation from "swr/mutation";
import { ServicesURL } from "@utils/utilsApi";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { formatDateWithMinutes } from "@utils/utilsDate";
import {
  EnrollmentConfigState,
  EnrollmentTargetsState,
  EnrollmentTriggerPayload,
} from "../types";
import { resolveExpirationKind } from "../config/enrollmentFieldsConfig";

export const buildCommonConfig = (config: EnrollmentConfigState) => {
  const isFixedDate = resolveExpirationKind(config) === "fixed-date";
  return {
    isAdmin: true,
    enrollDate: formatDateWithMinutes(new Date()),
    enrollType: config.enrollType,
    forcedExpiration: config.enableExpiration
      ? !!config.forcedExpiration
      : false,
    daysToComplete:
      config.enableExpiration && !isFixedDate ? config.daysToComplete : null,
    expirationDate:
      config.enableExpiration && isFixedDate && config.expirationDate
        ? formatDateWithMinutes(new Date(config.expirationDate)).split(" ")[0]
        : null,
    isExpirationFixed: config.enableExpiration ? isFixedDate : null,
    isExpirationHard: config.enableExpiration
      ? config.expirationType === "hard"
      : false,
    attributesIds: config.attributesIds ?? [],
  };
};

const buildUsersPayload = (
  trigger: EnrollmentTriggerPayload,
  targets: EnrollmentTargetsState,
  config: EnrollmentConfigState,
) => {
  const common = buildCommonConfig(config);
  return targets.users.map((u) => ({
    ...common,
    learningObject: {
      corporateId: Number(trigger.corporateId),
      objectId: trigger.objectId,
    },
    userId: u.id,
    domainId: null,
    massiveDomainId: null,
    massiveGroupId: null,
  }));
};

// Vecchia logica "una sola chiamata massive per tutti i gruppi selezionati".
// Mantenuta commentata per riferimento. La nuova logica fa N chiamate
// separate (una per gruppo) per poter passare il groupType corretto di ciascuno.
// const buildGroupsPayload = (
//   trigger: EnrollmentTriggerPayload,
//   targets: EnrollmentTargetsState,
//   config: EnrollmentConfigState,
// ) => ({
//   ...buildCommonConfig(config),
//   learningObject: {
//     corporateId: Number(trigger.corporateId),
//     forcedEnroll: true,
//     objectId: [trigger.objectId],
//   },
//   groupType: "DYNAMIC", //TODO togliere
//   massiveListGroupId: targets.groups.map((g) => Number(g.id)),
//   massiveListDomainId: null,
// });

const buildSingleGroupPayload = (
  trigger: EnrollmentTriggerPayload,
  group: { id: string | number; groupType?: string },
  config: EnrollmentConfigState,
) => ({
  ...buildCommonConfig(config),
  domainId: null,
  groupType: group.groupType,
  learningObject: {
    corporateId: Number(trigger.corporateId),
    forcedEnroll: true,
    objectId: [trigger.objectId],
  },
  massiveListDomainId: null,
  massiveListGroupId: [Number(group.id)],
});

const buildInitiativesPayload = (
  trigger: EnrollmentTriggerPayload,
  targets: EnrollmentTargetsState,
  config: EnrollmentConfigState,
) => ({
  ...buildCommonConfig(config),
  domainId: null,
  learningObject: {
    corporateId: Number(trigger.corporateId),
    objectId: trigger.objectId,
  },
  massiveListDomainId: targets.initiatives.map((i) => Number(i.id)),
  massiveListGroupId: null,
});

export interface SubmitArgs {
  trigger: EnrollmentTriggerPayload;
  targets: EnrollmentTargetsState;
  config: EnrollmentConfigState;
}

export interface SubmitResult {
  groups: { enrolledGroups: number; newEnrollments: number };
}

export const useEnrollmentSubmit = () => {
  const { trigger: postUsers, isMutating: isMutatingUsers } = useSWRMutation(
    ServicesURL.postEnrollment,
    (url: string, { arg }: { arg: any[] }) =>
      fetcher<any>(url, MethodType.POST, arg),
  );

  const { trigger: postGroups, isMutating: isMutatingGroups } = useSWRMutation(
    ServicesURL.postGroupEnrollmentMassive,
    (url: string, { arg }: { arg: any }) =>
      fetcher<any>(url, MethodType.POST, arg),
  );

  const { trigger: postInitiatives, isMutating: isMutatingInitiatives } =
    useSWRMutation(
      ServicesURL.postEnrollmentMassive,
      (url: string, { arg }: { arg: any }) =>
        fetcher<any>(url, MethodType.POST, arg),
    );

  const submit = async ({
    trigger,
    targets,
    config,
  }: SubmitArgs): Promise<SubmitResult> => {
    // Chiamate sequenziali

    // TODO verificare alternative possibili
    if (targets.users.length > 0) {
      await postUsers(buildUsersPayload(trigger, targets, config));
    }

    const groupsResult: SubmitResult["groups"] = {
      enrolledGroups: 0,
      newEnrollments: 0,
    };
    for (const g of targets.groups) {
      const res = await postGroups(buildSingleGroupPayload(trigger, g, config));
      groupsResult.enrolledGroups += 1;
      const newEnrollments = Number(res?.newEnrollments);
      if (Number.isFinite(newEnrollments) && newEnrollments > 0)
        groupsResult.newEnrollments += newEnrollments;
    }

    if (targets.initiatives.length > 0) {
      await postInitiatives(buildInitiativesPayload(trigger, targets, config));
    }

    return { groups: groupsResult };
  };

  return {
    submit,
    isSubmitting: isMutatingUsers || isMutatingGroups || isMutatingInitiatives,
  };
};
